import { useState, type ReactNode } from 'react'
import { DetailSection } from '../../../core/index.js'
import { formatWhen } from '../../components/FeedbackStatus.js'
import type { ChangeRequestView, Requirement, RequirementVersion } from '../../types.js'
import { actorLabel, latestRequirement } from './changeText.js'

function Block({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="mt-0.5 text-xs text-ink">{children}</div>
    </div>
  )
}

function Bullets({ items, empty = 'None' }: { items: readonly string[]; empty?: string }) {
  return items.length === 0 ? (
    <span className="text-ink-mid">{empty}</span>
  ) : (
    <ul className="list-disc pl-5">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}

/** One requirement, read-only, as the root approves it. */
export function RequirementText({ requirement }: { requirement: Requirement }) {
  return (
    <div className="flex flex-col gap-2.5">
      <Block label="Title">{requirement.title || <span className="text-ink-mid">Untitled</span>}</Block>
      <Block label="Problem">
        <p className="whitespace-pre-wrap break-words">{requirement.problem}</p>
      </Block>
      <Block label="Behaviour wanted">
        <p className="whitespace-pre-wrap break-words">{requirement.behaviour}</p>
      </Block>
      <Block label="Acceptance criteria">
        {requirement.acceptanceCriteria.length === 0 ? (
          <span className="text-ink-mid">None</span>
        ) : (
          <ol className="list-decimal pl-5">
            {requirement.acceptanceCriteria.map((criterion, index) => (
              <li key={index} className="whitespace-pre-wrap">
                {criterion.scenario}
                {criterion.cucumberSection && <span className="text-ink-mid"> · Cucumber: {criterion.cucumberSection}</span>}
              </li>
            ))}
          </ol>
        )}
      </Block>
      <div className="grid gap-2.5 md:grid-cols-2">
        <Block label="Scope">
          <Bullets items={requirement.scope} />
        </Block>
        <Block label="Out of scope">
          <Bullets items={requirement.outOfScope} />
        </Block>
      </div>
      <Block label="Policy decisions">
        <Bullets items={requirement.policyDecisions.map((decision) => `${decision.question} - ${decision.answer ?? 'open'}`)} />
      </Block>
      <Block label="Documents to update">
        <Bullets items={requirement.docsToUpdate} />
      </Block>
    </div>
  )
}

/** The requirement the change stands on now, with the version and the hash an approval names. */
export function RequirementView({ change }: { change: ChangeRequestView }) {
  const latest = latestRequirement(change)
  if (!latest) {
    return null
  }
  return (
    <DetailSection title={`Requirement · version ${latest.version}`}>
      <p className="mb-2 text-[11px] text-ink-mid">
        Content hash <span className="font-mono text-ink select-all break-all">{latest.contentHash}</span>
        {change.approval && change.approval.version === latest.version && <> · approved as this version</>}
      </p>
      <RequirementText requirement={latest.requirement} />
    </DetailSection>
  )
}

function VersionRow({ version, isOpen, onToggle }: { version: RequirementVersion; isOpen: boolean; onToggle: () => void }) {
  return (
    <li className="border-b border-marine/10 py-1.5 last:border-b-0">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="flex w-full flex-wrap items-baseline gap-x-3 text-left text-xs text-ink hover:text-marine">
        <span className="font-bold">Version {version.version}</span>
        <span className="text-ink-mid">
          {actorLabel(version.createdBy)} · {formatWhen(version.createdAt)}
        </span>
        <span className="ml-auto font-mono text-[10px] text-ink-mid">{version.contentHash.slice(0, 12)}…</span>
      </button>
      {isOpen && (
        <div className="mt-2 rounded border border-marine/10 bg-canvas p-2">
          <RequirementText requirement={version.requirement} />
        </div>
      )}
    </li>
  )
}

/** Every version the requirement has had, newest first; each opens to its text. */
export function VersionHistory({ change }: { change: ChangeRequestView }) {
  const [openVersion, setOpenVersion] = useState<number | null>(null)
  const versions = [...change.versions].reverse()
  return (
    <DetailSection title={`Versions · ${versions.length}`}>
      <ul>
        {versions.map((version) => (
          <VersionRow key={version.version} version={version} isOpen={openVersion === version.version} onToggle={() => setOpenVersion((current) => (current === version.version ? null : version.version))} />
        ))}
      </ul>
    </DetailSection>
  )
}
